import React, { useState, useCallback } from 'react';
import { Box, Tabs, Tab, Paper, Typography, useTheme } from '@mui/material';
import CoordinateInputPanel from './CoordinateInputPanel';
import GeoJsonEditorPanel from './GeoJsonEditorPanel';
import ExportPanel from './ExportPanel';
import InteractiveMap from './InteractiveMap';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';

function GeoJsonEditor() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [geoJsonData, setGeoJsonData] = useState({
    type: 'FeatureCollection',
    features: [],
  });
  const [resizeTrigger, setResizeTrigger] = useState(0);

  const handleGeoJsonChange = (data) => {
    if (data && data.type && Array.isArray(data.features)) {
      setGeoJsonData(data);
    }
  };

  const handleLayout = useCallback(() => {
    setResizeTrigger(c => c + 1);
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="GeoJSON Editor | Draw & Edit Map Data | UmbrellaCo"
        description="Free online interactive GeoJSON editor. Draw polygons, lines, and points. Edit coordinates directly and export spatial map data instantly."
        keywords="geojson editor, draw geojson online, create map polygons, spatial data editor"
        url="/spatial-tools/geojson-editor"
        jsonLd={createSoftwareAppSchema(
          'GeoJSON Editor',
          'Interactive online tool to draw and edit GeoJSON polygons, lines, and points.',
          'https://umbrellaco.in/spatial-tools/geojson-editor',
          ['Interactive Map Drawing', 'GeoJSON Modification', 'Coordinate Editing']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>GeoJSON Editor</Typography>
        <Typography variant="body2" color="text.secondary">
          Draw points, lines, and polygons directly on the map, edit the raw GeoJSON, and export your creations.
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <PanelGroup direction="horizontal" onLayout={handleLayout}>
          <Panel defaultSize={35} minSize={25}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
              >
                <Tab label="Add Data" />
                <Tab label="Edit GeoJSON" />
                <Tab label="Export" />
              </Tabs>
              <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
                {activeTab === 0 && (
                  <CoordinateInputPanel onGeoJsonChange={handleGeoJsonChange} currentGeoJson={geoJsonData} />
                )}
                {activeTab === 1 && (
                  <GeoJsonEditorPanel onGeoJsonChange={handleGeoJsonChange} currentGeoJson={geoJsonData} triggerResize={resizeTrigger} />
                )}
                {activeTab === 2 && (
                  <ExportPanel geoJsonData={geoJsonData} />
                )}
              </Box>
            </Paper>
          </Panel>
          <PanelResizeHandle style={{ width: '6px', background: theme.palette.divider, cursor: 'col-resize' }} />
          <Panel defaultSize={65} minSize={40}>
            <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
              <InteractiveMap
                geoJsonData={geoJsonData}
                onDrawChange={handleGeoJsonChange}
              />
            </Box>
          </Panel>
        </PanelGroup>
      </Box>
    </Box>
  );
}

export default GeoJsonEditor;
